import { useState } from 'react';
import { usePlatformStreamStore, type StreamingPlatform } from '@features/streaming';
import { useRoomStore } from '@features/room';
import { Panel, ActionButton } from '@shared/ui';

export function PlatformStreamSettings() {
  const destinations = usePlatformStreamStore((state) => state.destinations);
  const updateDestination = usePlatformStreamStore((state) => state.updateDestination);
  const saveSettingsToServer = usePlatformStreamStore((state) => state.saveSettingsToServer);
  const streamState = usePlatformStreamStore((state) => state.state);
  const tokenServerUrl = useRoomStore((state) => state.settings.tokenServerUrl);
  const participantId = useRoomStore((state) => state.participantId);

  const disabled = streamState === 'starting' || streamState === 'live' || streamState === 'stopping';
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const platforms: { id: StreamingPlatform; label: string; defaultUrl: string }[] = [
    { id: 'youtube', label: 'YouTube', defaultUrl: 'rtmp://a.rtmp.youtube.com/live2' },
    { id: 'twitch', label: 'Twitch', defaultUrl: 'rtmp://live.twitch.tv/app' },
    { id: 'kick', label: 'Kick', defaultUrl: 'rtmps://fa723fc1b171.global-contribute.live-video.net/app' },
    { id: 'custom', label: 'Custom RTMP', defaultUrl: 'rtmp://live.example.com/app' },
  ];

  const handleSave = async () => {
    if (!participantId) {
      setSaveStatus('error');
      setErrorMessage('Join a room before saving stream settings');
      return;
    }
    setSaveStatus('saving');
    setErrorMessage(null);
    try {
      for (const platform of platforms) {
        const dest = destinations[platform.id];
        if (dest.enabled && !dest.rtmpUrl.trim()) {
          updateDestination(platform.id, { rtmpUrl: platform.defaultUrl });
        }
      }
      await saveSettingsToServer(tokenServerUrl, participantId);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error(error);
      setSaveStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Failed to save settings');
    }
  };

  return (
    <div className="space-y-6">
      {platforms.map((platform) => {
        const dest = destinations[platform.id] || { rtmpUrl: '', streamKey: '' };
        return (
          <Panel key={platform.id} title={`${platform.label.toUpperCase()} CONFIGURATION`}>
            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-text-secondary">
                  RTMP Server URL
                </label>
                <input
                  type="text"
                  value={dest.rtmpUrl}
                  placeholder={platform.defaultUrl || 'rtmp://live.example.com/app'}
                  disabled={disabled}
                  onChange={(e) =>
                    updateDestination(platform.id, { rtmpUrl: e.target.value })
                  }
                  className="w-full rounded-lg border border-surface-border bg-surface-2 px-3 py-2 text-sm text-text-primary placeholder-text-muted outline-none focus:border-accent disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium text-text-secondary">
                  Stream Key / Token
                </label>
                <input
                  type="password"
                  value={dest.streamKey}
                  placeholder="Paste stream key here"
                  disabled={disabled}
                  onChange={(e) =>
                    updateDestination(platform.id, { streamKey: e.target.value })
                  }
                  className="w-full rounded-lg border border-surface-border bg-surface-2 px-3 py-2 text-sm text-text-primary placeholder-text-muted outline-none focus:border-accent disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>
            </div>
          </Panel>
        );
      })}

      <div className="flex flex-col gap-2 pt-2">
        <ActionButton
          variant="accent"
          disabled={disabled || saveStatus === 'saving'}
          onClick={() => void handleSave()}
          className="w-full font-semibold shadow-md transition-all active:scale-[0.98]"
        >
          {saveStatus === 'saving'
            ? 'Saving to server...'
            : saveStatus === 'success'
              ? '✓ Settings Saved!'
              : 'Save to Server'}
        </ActionButton>

        {saveStatus === 'error' && errorMessage && (
          <p className="rounded-lg border border-status-live/30 bg-status-live/10 px-3 py-2 text-xs text-status-live">
            {errorMessage}
          </p>
        )}
      </div>
    </div>
  );
}
